<script setup lang="ts">
import { useDebounceFn } from "@vueuse/core";
import ContinuousBarcodeScanner from "@/components/shared/ContinuousBarcodeScanner.vue";
import MobilePageTitle from "@/components/shared/mobile/MobilePageTitle.vue";
import StockCheckerResultCard from "@/components/warehouse/StockCheckerResultCard.vue";
import { useBarcodeWedge } from "@/composables/useBarcodeWedge";
import { matchDriverCode } from "@/constants/barcode";
import { useMobileShell } from "@/composables/useMobileShell";
import { useStockCheckerLookup } from "@/composables/useStockCheckerLookup";
import type { ContinuousBarcodeScannerFeedback, StockCountItem } from "@/type/stockCount";

/**
 * Stock Checker (استعلام الرصيد) — where is this item, and how much of it?
 *
 * The read-only twin of stocktaking. Same phone-first column and the same three
 * input routes, but nothing is ever written: scan a code, and the item's total
 * quantity and stock value appear with a per-warehouse breakdown beneath.
 *
 * ⚠️ ONE RESULT AT A TIME, and a new scan REPLACES the last — including a scan
 * that matches nothing. Keeping the previous card visible under a "no match"
 * banner would leave live-looking numbers on screen belonging to a DIFFERENT
 * item, in front of an operator whose eyes are on the shelf. Losing a good
 * result to a stray scan of a shipping label costs one re-scan; a misread
 * quantity costs a wrong decision.
 *
 * This is why the page has no scan queue where stocktaking has one — see
 * `useStockCheckerLookup`, which supersedes in-flight lookups instead.
 */

definePage({
  meta: {
    // definePage meta is hoisted out of component scope at compile time, so it
    // must be a static literal — a runtime import like PAGE_KEYS would throw
    // "PAGE_KEYS is not defined". Value mirrors PAGE_KEYS.WAREHOUSE_STOCK_CHECKER.
    permissionKey: "page.warehouse.stock_checker",
  },
});

const { t, locale } = useI18n();
const { isMobileShell } = useMobileShell();

const numberFormatter = computed(() =>
  new Intl.NumberFormat(locale.value === "ar" ? "ar-IQ-u-nu-latn" : "en-US", {
    maximumFractionDigits: 2,
  }),
);

const {
  state,
  visibleRows,
  showAllWarehouses,
  warehouseListAvailable,
  searchLoading,
  ensureWarehouses,
  lookup,
  lookupItem,
  searchItems,
  retry,
} = useStockCheckerLookup();

const cameraActive = ref(false);
const manualOpen = ref(false);
const manualQuery = ref("");
const manualResults = ref<StockCountItem[]>([]);

const searching = computed(() => state.value.status === "searching");

/**
 * The chip drawn over the camera preview after a read.
 *
 * Derived by WATCHING the lookup state rather than returned by the composable,
 * which keeps that composable free of `t()` and formatting. Feedback is the
 * scanner's own overlay, never a toast: a toast stacks and has to be dismissed,
 * and an operator sweeping a shelf produces one of these every second or so.
 */
const feedback = ref<ContinuousBarcodeScannerFeedback | null>(null);
let feedbackTimer: ReturnType<typeof setTimeout> | null = null;

function showFeedback(next: ContinuousBarcodeScannerFeedback) {
  feedback.value = next;

  if (feedbackTimer)
    clearTimeout(feedbackTimer);

  feedbackTimer = setTimeout(() => {
    feedback.value = null;
    feedbackTimer = null;
  }, 2600);
}

onBeforeUnmount(() => {
  if (feedbackTimer)
    clearTimeout(feedbackTimer);
});

watch(state, (next) => {
  if (next.status === "not-found") {
    // A driver's personal QR is not an item: say what it is and where it works.
    if (matchDriverCode(next.barcode)) {
      showFeedback({ tone: "error", text: t("alkokh.driverQr.notAnItem") });

      return;
    }

    showFeedback({ tone: "error", text: t("alkokh.stockChecker.feedback.notFound", { barcode: next.barcode }) });

    return;
  }

  if (next.status === "failed") {
    showFeedback({ tone: "error", text: t("alkokh.stockChecker.feedback.failed") });

    return;
  }

  if (next.status !== "found")
    return;

  // ⚠️ A failed stock read is a WARNING, not a success — reporting the item as
  // found with a quantity of zero is the one thing this page must never do.
  showFeedback(next.rows === null
    ? { tone: "warning", text: t("alkokh.stockChecker.states.stockUnavailable") }
    : {
        tone: "success",
        text: t("alkokh.stockChecker.feedback.found", {
          item: next.item.itemName,
          qty: numberFormatter.value.format(next.totalQty || 0),
        }),
      });
});

/**
 * A hardware gun works on the plain-HTTP deployment where the camera does not,
 * so on a desktop this is the primary input, not an accessory.
 *
 * No `enabled` gate: unlike a count, nothing has to be chosen before a lookup is
 * valid. The wedge already leaves a human's typing and Enter alone, so the
 * manual field below keeps working while this is armed.
 */
useBarcodeWedge({ onScan: code => void lookup(code) });

onMounted(() => {
  // Fetched once, not per scan, so the "show all warehouses" toggle is instant.
  // A failure here only costs the zero rows; the lookup itself still works.
  void ensureWarehouses();
});

function onDecoded(code: string) {
  void lookup(code);
}

function onScannerUnavailable() {
  // The camera is genuinely unavailable on the plain-HTTP deployment, so manual
  // entry is opened for the operator rather than left for them to find. This is
  // the expected path here, not an edge case.
  manualOpen.value = true;
}

const runManualSearch = useDebounceFn(async (term: string) => {
  manualResults.value = term.trim().length >= 2
    ? await searchItems(term)
    : [];
}, 300);

watch(manualQuery, (term) => {
  void runManualSearch(term ?? "");
});

/** Enter in the manual field means "this is a code", not "search for this". */
function submitManualCode() {
  const code = manualQuery.value?.trim();
  if (!code)
    return;

  void lookup(code);
  manualResults.value = [];
}

/** A picked search result is already resolved, so it skips the barcode legs. */
function selectManualResult(item: StockCountItem) {
  void lookupItem(item);
  manualResults.value = [];
}

function manualSubtitle(item: StockCountItem) {
  return [item.itemCode, item.stockUom].filter(Boolean).join(" · ");
}

/** From the not-found card: hand the failed code to the search box. */
function searchByName(barcode: string) {
  manualOpen.value = true;
  manualQuery.value = barcode;
}
</script>

<template>
  <div class="stock-checker-page">
    <div class="stock-checker-page__column">
      <MobilePageTitle
        :title="t('alkokh.stockChecker.title')"
        :subtitle="t('alkokh.stockChecker.subtitle')"
      />

      <VAlert
        v-if="!isMobileShell"
        type="info"
        variant="tonal"
        density="compact"
        class="mb-4"
      >
        {{ t("alkokh.stockChecker.desktopNotice") }}
      </VAlert>

      <!-- The scanner stays pinned: the operator's hands are busy and it must
           not scroll away as a long warehouse list grows below it. -->
      <div class="stock-checker-page__sticky">
        <ContinuousBarcodeScanner
          v-model:active="cameraActive"
          :feedback="feedback"
          repeat-mode="cooldown"
          @decoded="onDecoded"
          @unavailable="onScannerUnavailable"
        />

        <div class="stock-checker-page__manual-toggle">
          <VBtn
            variant="tonal"
            block
            :prepend-icon="manualOpen ? 'tabler-x' : 'tabler-search'"
            @click="manualOpen = !manualOpen"
          >
            {{ t("alkokh.stockChecker.manual.toggle") }}
          </VBtn>
        </div>

        <VExpandTransition>
          <div
            v-if="manualOpen"
            class="stock-checker-page__manual"
          >
            <AppTextField
              v-model="manualQuery"
              :label="t('alkokh.stockChecker.manual.label')"
              :placeholder="t('alkokh.stockChecker.manual.placeholder')"
              :loading="searchLoading"
              density="comfortable"
              clearable
              @keyup.enter="submitManualCode"
            />

            <VList
              v-if="manualResults.length"
              class="stock-checker-page__manual-results"
              density="compact"
            >
              <VListItem
                v-for="item in manualResults"
                :key="item.itemCode"
                :title="item.itemName"
                :subtitle="manualSubtitle(item)"
                @click="selectManualResult(item)"
              >
                <template #append>
                  <VIcon
                    icon="tabler-search"
                    size="18"
                  />
                </template>
              </VListItem>
            </VList>

            <p class="stock-checker-page__manual-hint">
              {{ t("alkokh.stockChecker.manual.hint") }}
            </p>
          </div>
        </VExpandTransition>

        <VProgressLinear
          v-if="searching"
          indeterminate
          color="primary"
          class="mt-2"
        />
      </div>

      <StockCheckerResultCard
        v-model:show-all="showAllWarehouses"
        :state="state"
        :rows="visibleRows"
        :warehouse-list-available="warehouseListAvailable"
        @retry="retry"
        @search-by-name="searchByName"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.stock-checker-page {
  display: flex;
  justify-content: center;

  &__column {
    /* One phone-width column at every size, mirroring stocktaking: the operator
       walks the aisle holding the phone, and desktop centres the same column
       rather than spreading into a second design to maintain. */
    inline-size: 100%;
    max-inline-size: 30rem;
  }

  &__sticky {
    position: sticky;
    /* Clears the phone shell's app bar; on desktop it clears the navbar. */
    inset-block-start: 0;
    z-index: 2;
    padding-block-end: 0.75rem;
    background: rgb(var(--v-theme-background));
  }

  &__manual-toggle {
    margin-block-start: 0.75rem;
  }

  &__manual {
    margin-block-start: 0.625rem;
  }

  &__manual-results {
    margin-block-start: 0.375rem;
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 8px;
    max-block-size: 14rem;
    overflow-y: auto;
  }

  &__manual-hint {
    margin-block-start: 0.375rem;
    margin-block-end: 0;
    color: rgba(var(--v-theme-on-surface), 0.6);
    font-size: 0.75rem;
    line-height: 1.35;
  }
}
</style>
